import type {
  AgentToClientMessage,
  ProcessStatus,
  SessionStatus,
} from "schemas/generated/agentConnection";
import type { AgentPermissionPromptReply } from "schemas/generated/agentPermissions";
import type {
  OpencodeSourceContext,
  SessionMetadata,
} from "schemas/generated/agentSession";
import type {
  OpencodeCommit,
  OpencodeCommitFileChange,
} from "schemas/generated/agentTools/opencode";
import type {
  ReploOpencodeHarnessInstallRegistryItemsRpcOutput,
  ReploOpencodeHarnessInstallRegistryItemsToolInput,
} from "schemas/generated/agentTools/reploOpencodeHarness";
import type {
  ComponentPreviewIndex,
  ComponentPreviewPreparation,
  ComponentPreviewSource,
} from "schemas/generated/componentPreview";
import type { UIMessagePart } from "schemas/generated/mentionable";
import type {
  PageAuditScanBundle,
  PageAuditScanRequest,
} from "schemas/generated/pageAuditScan";
import type {
  SandboxFile,
  WorkspaceSkill,
} from "schemas/generated/sandboxFiles";
import type { SandboxSite } from "schemas/generated/sites";
import type { BedrockClient } from "../client";
import type { FsMoveInput, FsWriteAtomicInput } from "../fs";
import type { SandboxFileEncoding } from "../sandbox-files";
import type { SessionStreamSnapshot } from "./session-stream";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { shallowEqual } from "replo-utils/lib/object";
import { agentToClientMessageSchema } from "schemas/agentConnection";
import { v4 as uuidv4 } from "uuid";

import { BedrockApiRequestError } from "../errors";
import { SessionStreamManager } from "./session-stream";

export type AgentConnectionState = "connecting" | "connected" | "disconnected";
export type ConnectionStateChangeHandler = (
  state: AgentConnectionState,
  previousState: AgentConnectionState,
) => void;
type MessageHandler = (message: Record<string, unknown>) => void;

export type SessionStateEntry = {
  sessionId: string;
  metadata: SessionMetadata;
  status: SessionStatus | null;
};

export type SandboxState = NonNullable<
  Extract<AgentToClientMessage, { type: "sandbox-state" }>["state"]
>;

// Deep scans verify links and click-probe buttons, outlasting the 60s default.
// Deliberately longer than the coordinator's 120s exec budget so the server's
// own error surfaces instead of a bare client timeout — but the two clocks
// start at different moments (the server's only covers `sandbox.exec`, after
// sandbox wake-up), so on a cold sandbox this one can still fire first.
const DEEP_PAGE_AUDIT_SCAN_TIMEOUT_MS = 130_000;

export interface AgentConnection {
  callMethod<ResponseType>(
    method: string,
    args: unknown[],
    options?: { timeoutMs?: number },
  ): Promise<ResponseType>;
  close(): void;
}

export class BedrockAgent {
  private connection: AgentConnection | null = null;
  private readonly sessionStreams = new SessionStreamManager();
  // NOTE (Max, 2026-04-29): Raw WebSocket message handlers registered via onMessage().
  // Mini-apps use this to listen for coordinator events (e.g. opencode-event) and
  // route them into app-level state (e.g. opencodeEventStore in hello-world).
  private readonly messageHandlers = new Set<MessageHandler>();
  // NOTE (Max, 2026-04-29): Connection state lives here (pure TS) instead of in
  // BedrockProvider's context so that only components calling useAgentConnectionState()
  // re-render on connection changes — not every useBedrock() consumer.
  private readonly connectionStateListeners =
    new Set<ConnectionStateChangeHandler>();
  private _connectionState: AgentConnectionState = "connecting";
  // NOTE (Max, 2026-06-11): Session status + metadata mirrored from the DO's
  // `user-state` broadcast, plus optimistic seeds for our own in-flight
  // sessionPrompt calls (rolled back on RPC failure) so panels render on click
  // instead of after the broadcast echo. Consumers subscribe via
  // useSessionStatus / useSessionStateEntries without their own onMessage listener.
  // The map is replaced wholesale on change, never mutated, so its reference
  // doubles as the useSyncExternalStore snapshot: same reference means
  // nothing changed.
  private sessionIdToStateEntry: ReadonlyMap<string, SessionStateEntry> =
    new Map();
  private readonly sessionStateEntryListeners = new Set<() => void>();
  // NOTE (Max, 2026-06-21): Latest sandbox state mirrored from the DO's
  // `sandbox-state` broadcast. Retained so consumers read the current value via
  // useSandboxState regardless of mount timing: the DO pushes the snapshot the
  // instant the socket connects, before a late `onMessage` subscriber attaches,
  // so a raw listener would miss it.
  private sandboxState: SandboxState | null = null;
  private readonly sandboxStateListeners = new Set<() => void>();

  constructor(private readonly client: BedrockClient) {}

  get connectionState(): AgentConnectionState {
    return this._connectionState;
  }

  // NOTE (Max, 2026-04-29): Used by useAgentConnectionState() as the subscribe function
  // for useSyncExternalStore. React calls this to register a callback, and we notify it
  // (via setAgentConnectionState) when the connection state changes so it re-reads the snapshot.
  onAgentConnectionStateChange(
    callback: ConnectionStateChangeHandler,
  ): () => void {
    this.connectionStateListeners.add(callback);
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  // NOTE (Max, 2026-04-29): WebSocket can fire both onClose and onError for the same
  // disconnect, both setting "disconnected". The early return deduplicates so listeners
  // (and therefore useSyncExternalStore / React) only fire once per actual transition.
  setAgentConnectionState(state: AgentConnectionState) {
    if (this._connectionState === state) {
      return;
    }
    const previousState = this._connectionState;
    this._connectionState = state;
    for (const listener of this.connectionStateListeners) {
      listener(state, previousState);
    }
  }

  connect() {
    this.connection = this.client.connectRealtime({
      autoReconnect: true,
      onOpen: () => {
        this.setAgentConnectionState("connected");
      },
      onClose: () => {
        this.handleDisconnect(new Error("WebSocket closed"));
      },
      onError: () => {
        this.handleDisconnect(new Error("WebSocket error"));
      },
      onRawMessage: (message) => {
        this.handleRawMessage(message);
      },
    });
    this.setAgentConnectionState("connecting");
  }

  close() {
    this.connection?.close();
    this.connection = null;
    this.handleDisconnect(new Error("Agent connection closed"));
  }

  handleDisconnect(error: Error) {
    this.sessionStreams.rejectAll(error);
    this.setAgentConnectionState("disconnected");
  }

  setConnection(connection: AgentConnection) {
    this.connection = connection;
  }

  clearConnection() {
    this.connection = null;
    this.handleDisconnect(new Error("Agent connection closed"));
  }

  handleRawMessage(message: unknown) {
    if (typeof message !== "object" || message === null) {
      return;
    }
    // eslint-disable-next-line replo/no-as-cast -- bulk-suppression added during the registry-realignment / main rebase to match the eaa1853c50 pattern; revisit per cast and replace with proper Zod / type-narrowing where possible.
    const msg = message as Record<string, unknown>;
    for (const handler of this.messageHandlers) {
      handler(msg);
    }
    this.sessionStreams.routeEvent(msg);

    // NOTE (Max, 2026-06-21): Parse each frame once and dispatch the two
    // latest-wins state messages (user-state and sandbox-state) to their appliers.
    const parsedMessage = agentToClientMessageSchema.safeParse(msg);
    if (
      !parsedMessage.success ||
      (parsedMessage.data.type !== "user-state" &&
        parsedMessage.data.type !== "sandbox-state")
    ) {
      return;
    }
    exhaustiveSwitch(parsedMessage.data)({
      "user-state": (data) => this.applySessionState(data),
      "sandbox-state": (data) => this.applySandboxState(data),
    });
  }

  private applySessionState(
    data: Extract<AgentToClientMessage, { type: "user-state" }>,
  ) {
    const sessions = data.state.sessions;
    if (!sessions) {
      return;
    }
    const nextSessionStateMap = new Map(this.sessionIdToStateEntry);
    let hasSessionStateChanged = false;
    for (const [sessionId, session] of Object.entries(sessions)) {
      const previousState = this.sessionIdToStateEntry.get(sessionId);
      // NOTE (Max, 2026-06-12): A null status in the broadcast means "no status yet", not "status
      // cleared" — keep the last known status so consumers don't flicker.
      const status = session.status ?? previousState?.status ?? null;
      if (status === "error" && previousState?.status !== "error") {
        this.sessionStreams.cancelStream(
          sessionId,
          new Error("Agent session failed."),
        );
      }
      // NOTE (Max, 2026-06-12): Every SessionMetadata variant is a flat object
      // of scalars, so shallow equality is an exact change check.
      const hasMetadataChanged = !shallowEqual(
        previousState?.metadata,
        session.metadata,
      );

      const hasStatusChanged = previousState?.status !== status;
      const shouldUpdateSessionStateMap =
        hasMetadataChanged || hasStatusChanged;
      if (shouldUpdateSessionStateMap) {
        nextSessionStateMap.set(sessionId, {
          sessionId,
          metadata: session.metadata,
          status,
        });
        hasSessionStateChanged = true;
      }
    }
    if (hasSessionStateChanged) {
      this.commitSessionStateMap(nextSessionStateMap);
    }
  }

  private replaceSessionInMap(entry: SessionStateEntry) {
    const nextSessionStateMap = new Map(this.sessionIdToStateEntry);
    nextSessionStateMap.set(entry.sessionId, entry);
    this.commitSessionStateMap(nextSessionStateMap);
  }

  private removeSessionFromMap(sessionId: string) {
    const nextSessionStateMap = new Map(this.sessionIdToStateEntry);
    nextSessionStateMap.delete(sessionId);
    this.commitSessionStateMap(nextSessionStateMap);
  }

  // NOTE (Max, 2026-06-12): The one place the map is swapped. Committing a
  // state transition includes making it observable, so every mutation path
  // funnels through here and can't forget to notify.
  private commitSessionStateMap(
    nextSessionStateMap: ReadonlyMap<string, SessionStateEntry>,
  ) {
    this.sessionIdToStateEntry = nextSessionStateMap;
    for (const listener of this.sessionStateEntryListeners) {
      listener();
    }
  }

  getSessionStatus(sessionId: string): SessionStatus | null {
    return this.sessionIdToStateEntry.get(sessionId)?.status ?? null;
  }

  // NOTE (Max, 2026-06-12): All sessions seen in the `user-state` broadcast, with their current
  // metadata + status. Consumers (e.g. a page-operations panel) filter by
  // metadata.type to rebuild their list after a reload.
  getSessionStateMap(): ReadonlyMap<string, SessionStateEntry> {
    return this.sessionIdToStateEntry;
  }

  subscribeToSessionState(listener: () => void): () => void {
    this.sessionStateEntryListeners.add(listener);
    return () => {
      this.sessionStateEntryListeners.delete(listener);
    };
  }

  private applySandboxState(
    data: Extract<AgentToClientMessage, { type: "sandbox-state" }>,
  ) {
    this.sandboxState = data.state ?? null;
    for (const listener of this.sandboxStateListeners) {
      listener();
    }
  }

  getSandboxState(): SandboxState | null {
    return this.sandboxState;
  }

  subscribeToSandboxState(listener: () => void): () => void {
    this.sandboxStateListeners.add(listener);
    return () => {
      this.sandboxStateListeners.delete(listener);
    };
  }

  // NOTE (Max, 2026-04-29): Clients subscribe to all raw WebSocket messages via
  // client.agent.onMessage().
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  // ---------------------------------------------------------------------------
  // RPC actions
  // ---------------------------------------------------------------------------

  async sessionCreate(params: { sessionId: string; type?: string }) {
    return this.rpc<{ sessionId: string; opencodeSessionId: string }>(
      "sessionCreate",
      [params],
    );
  }

  async sessionPrompt(params: {
    sessionId: string;
    parts: UIMessagePart[];
    metadata: SessionMetadata;
    sourceContext: OpencodeSourceContext;
    systemContext?: string;
    /** Client-generated message UUID; coordinator forwards to opencode and pre-persists for a stable id across the SSE roundtrip. */
    messageId?: string;
  }) {
    const previousState = this.sessionIdToStateEntry.get(params.sessionId);
    // NOTE (Max, 2026-06-12): Seed the mirror optimistically: the payload is exactly what the DO will
    // store and echo back via `user-state`, so panels render on click and the
    // echo overwrites with identical values (no flicker).
    this.replaceSessionInMap({
      sessionId: params.sessionId,
      metadata: params.metadata,
      status: "pending",
    });
    try {
      const result = await this.rpc<{ sessionId: string }>("sessionPrompt", [
        params,
      ]);
      // NOTE: When the coordinator reuses an active session (e.g. via
      // reuseIdentifier), the prompt runs and broadcasts under the reused id,
      // not the client-generated one we optimistically seeded. Drop the
      // orphaned seed so it doesn't linger as a phantom streaming session.
      if (result.sessionId !== params.sessionId) {
        this.removeSessionFromMap(params.sessionId);
      }
      return result;
    } catch (error) {
      // NOTE (Max, 2026-06-12): Roll back so a failed request doesn't leave a phantom spinner.
      if (previousState) {
        this.replaceSessionInMap(previousState);
      } else {
        this.removeSessionFromMap(params.sessionId);
      }
      throw error;
    }
  }

  async sessionAbort(params: { sessionId: string }) {
    return this.rpc<{ ok: boolean }>("sessionAbort", [params]);
  }

  async answerQuestion(params: { sessionId: string; answers: string[][] }) {
    return this.rpc<void>("answerQuestion", [params]);
  }

  // Debug affordance: force a "dreaming" memory curation pass now.
  async triggerMemoryCuration() {
    return this.rpc<{ ok: true; sessionId: string }>(
      "triggerMemoryCuration",
      [],
    );
  }

  async replyToPermission(params: {
    sessionId: string;
    permissionId: string;
    reply: AgentPermissionPromptReply;
  }) {
    return this.rpc<{ ok: boolean }>("replyToPermission", [params]);
  }

  async readFile(params: { path: string; encoding?: SandboxFileEncoding }) {
    return this.rpc<{ content: string; sizeBytes: number; revision: string }>(
      "readFile",
      [params],
    );
  }

  // Batch read: pass an array of paths, get back a path -> content map in one
  // round trip. A file that exists but is empty is "", a file that does not
  // exist is null (unlike readFile, which collapses missing into "").
  async readFiles(params: { paths: string[]; encoding?: SandboxFileEncoding }) {
    return this.rpc<{ files: Record<string, string | null> }>("readFiles", [
      params,
    ]);
  }

  async writeFile(params: {
    path: string;
    content: string;
    encoding?: SandboxFileEncoding;
  }) {
    return this.rpc<{ sizeBytes: number; revision: string }>("writeFile", [
      params,
    ]);
  }

  async writeFileAtomic(params: FsWriteAtomicInput) {
    return this.rpc<{ sizeBytes: number; revision: string }>(
      "writeFileAtomic",
      [params],
    );
  }

  async moveFile(params: FsMoveInput) {
    return this.rpc<{ ok: true }>("moveFile", [params]);
  }

  async listFiles() {
    return this.rpc<{ files: { path: string }[] }>("listFiles", []);
  }

  async findFiles(params: {
    patterns: string[];
    directory: string;
    ignore?: string[];
  }) {
    return this.rpc<{ files: SandboxFile[] }>("findFiles", [params]);
  }

  async listSandboxSkills() {
    return this.rpc<{ skills: WorkspaceSkill[] }>("listSandboxSkills", []);
  }

  // Site identity + provisioning under `client.agent.sites`: `find` lists the project's sites joined against on-disk state; `provision` forks a new site or clones an existing project site onto this sandbox, streaming progress over the public `process-log` channel.
  get sites() {
    return {
      find: () => this.rpc<SandboxSite[]>("findSites", []),
      // Lists a site's page files resolved server-side from the siteId pin, so callers never synthesize a `dev/<dir>` path; returns 0-N (empty when the site isn't on this sandbox yet).
      findPages: ({ siteId }: { siteId: string }) => {
        return this.rpc<{ files: SandboxFile[] }>("findSitePages", [
          { siteId },
        ]);
      },
      ensureContentCollectionRoutes: ({
        siteId,
        collectionSlug,
        includeListingPage,
      }: {
        siteId: string;
        collectionSlug: string;
        includeListingPage?: boolean;
      }) => {
        return this.rpc<
          // `converging` = the managed-package converge delivering the content
          // runtime is still installing; poll until `ready`.
          { status: "converging" } | { status: "ready"; createdPaths: string[] }
        >("ensureContentCollectionRoutes", [
          { siteId, collectionSlug, includeListingPage },
        ]);
      },
      renameContentCollectionRoute: ({
        siteId,
        fromSlug,
        toSlug,
      }: {
        siteId: string;
        fromSlug: string;
        toSlug: string;
      }) => {
        return this.rpc<
          | { moved: false }
          | {
              moved: true;
              sourceDirectory: string;
              destinationDirectory: string;
            }
        >("renameContentCollectionRoute", [{ siteId, fromSlug, toSlug }]);
      },
      provision: (params: {
        subdomain?: string;
        directory?: string;
        name?: string;
        template?: string;
        siteId?: string;
      }) => {
        // `status: "provisioning"` means the coordinator returned before the slow
        // clone+install finished (it runs that in the background to dodge the 60s
        // realtime RPC timeout), so callers must poll on-disk readiness after this
        // resolves. See runDeterministicProvision. (Noah, 2026-07-08, REPL-28979)
        return this.rpc<{
          siteId: string;
          remoteUrl: string | null;
          status: "provisioning" | "ready";
        }>("provisionSite", [params]);
      },
      // Un-stops a single installed site's dev server; idempotent (no-op if already ready). The website builder auto-calls this when it opens a site whose server reads as stopped. Blocks server-side until the server attests ready (or a timeout), returning the final readiness so the caller can trust a fresh start before the sandbox-state broadcast catches up.
      startDevServerIfNeeded: ({
        siteId,
        allowFatalRestart,
      }: {
        siteId: string;
        allowFatalRestart: boolean;
      }) => {
        return this.rpc<{ status: ProcessStatus | null; ready: boolean }>(
          "startSiteDevServerIfNeeded",
          [{ siteId, allowFatalRestart }],
        );
      },
      // Tears the site down end-to-end (soft-delete row, delete Artifacts repo, remove the on-disk tree).
      delete: ({ siteId }: { siteId: string }) => {
        return this.rpc<{ ok: true }>("deleteSite", [{ siteId }]);
      },
    };
  }

  get siteComponents() {
    return {
      find: ({ siteId }: { siteId: string }) => {
        return this.rpc<ComponentPreviewIndex>("findSiteComponents", [
          { siteId },
        ]);
      },
      getSource: ({
        siteId,
        componentPath,
      }: {
        siteId: string;
        componentPath: string;
      }) => {
        return this.rpc<ComponentPreviewSource>("getSiteComponentSource", [
          { siteId, componentPath },
        ]);
      },
      preparePreview: ({
        siteId,
        componentPath,
      }: {
        siteId: string;
        componentPath: string;
      }) => {
        return this.rpc<ComponentPreviewPreparation>(
          "prepareSiteComponentPreview",
          [{ siteId, componentPath }],
        );
      },
    };
  }

  // Deterministic install for mechanical registry types (skill, plan, page,
  // site): runs the install CLI in the sandbox with no agent turn. Section,
  // branding, and product installs need agent judgment — send those to chat.
  //
  // Retry warning: the client RPC timeout (REALTIME_RPC_TIMEOUT_MS) is
  // shorter than the sandbox exec cap (EXEC_TIMEOUT_SECONDS), so a slow
  // install can reject here while still succeeding server-side. Page installs
  // are not idempotent — re-running one collision-suffixes the route — so
  // don't blindly retry on timeout. Use `isInstallOutcomeUnknownError` to
  // special-case timeouts.
  get registryItems() {
    return {
      install: (params: ReploOpencodeHarnessInstallRegistryItemsToolInput) => {
        return this.rpc<ReploOpencodeHarnessInstallRegistryItemsRpcOutput>(
          "installRegistryItems",
          [params],
        );
      },
    };
  }

  // Deterministic page scan with no agent session: the coordinator execs the
  // bundled page-audit-scan CLI and relays its evidence bundle.
  async scanPageAudit(
    params: PageAuditScanRequest,
  ): Promise<PageAuditScanBundle> {
    return this.rpc<PageAuditScanBundle>(
      "runPageAuditScan",
      [params],
      params.depth === "deep"
        ? { timeoutMs: DEEP_PAGE_AUDIT_SCAN_TIMEOUT_MS }
        : undefined,
    );
  }

  // TODO (Max, 2026-05-10, REPL-26971): scope is hardcoded to "userApp"
  // here — the DO & supervisor's birpc still expects this param to be userApp,
  // even though it's outdated. This will get refactored in REPL-26971.
  async getGitHistory(params: {
    siteId: string;
    limit?: number;
    skip?: number;
  }) {
    return this.rpc<{
      commits: OpencodeCommit[];
      hasMore: boolean;
      nextSkip: number;
    }>("getGitHistory", [{ ...params, scope: "userApp" }]);
  }

  // (Cole, 2026-07-14, REPL-29286): omit endRef to diff against the working tree, including untracked files.
  async getCommitDiff(params: {
    siteId: string;
    startSha: string;
    endRef?: string;
  }) {
    return this.rpc<{ files: OpencodeCommitFileChange[] }>("getCommitDiff", [
      { ...params, scope: "userApp" },
    ]);
  }

  async getLastPublishedCommitSha(params: { siteId: string }) {
    return this.rpc<{
      sha: string;
      hasBeenPublished: boolean;
      publishedAt?: string | null;
    }>("getLastPublishedCommitSha", [params]);
  }

  async revertToCommit(params: { siteId: string; commit: OpencodeCommit }) {
    return this.rpc<{ ok: boolean; error?: string }>("revertToCommit", [
      params,
    ]);
  }

  async deleteFile(params: { path: string }) {
    return this.rpc<{ ok: true }>("deleteFile", [params]);
  }

  async killProcess(params: { processName: string }) {
    return this.rpc<{ killed: boolean }>("killProcess", [params]);
  }

  async resetProject() {
    return this.rpc<{ ok: boolean; message: string }>("resetProject", []);
  }

  // ---------------------------------------------------------------------------
  // Session streaming
  // ---------------------------------------------------------------------------

  private getSubmittedPromptTexts({
    parts,
    prompt,
  }: {
    parts?: UIMessagePart[];
    prompt: string;
  }) {
    if (!parts) {
      return prompt.trim() ? [prompt] : [];
    }

    return parts.flatMap((part) => {
      if (part.type !== "text" || !part.text.trim()) {
        return [];
      }
      return [part.text];
    });
  }

  async streamSession(params: {
    prompt: string;
    parts?: UIMessagePart[];
    metadata: SessionMetadata;
    sourceContext: OpencodeSourceContext;
    sessionId?: string;
    systemContext?: string;
    onSnapshot?: (snapshot: SessionStreamSnapshot) => void;
  }): Promise<SessionStreamSnapshot> {
    const sessionId = params.sessionId ?? uuidv4();
    const resultPromise = this.sessionStreams.createStream({
      sessionId,
      onSnapshot: params.onSnapshot,
      submittedPromptTexts: this.getSubmittedPromptTexts({
        parts: params.parts,
        prompt: params.prompt,
      }),
    });
    try {
      const { sessionId: resolvedSessionId } = await this.sessionPrompt({
        sessionId,
        parts: params.parts ?? [{ type: "text", text: params.prompt }],
        metadata: params.metadata,
        sourceContext: params.sourceContext,
        systemContext: params.systemContext,
      });

      // NOTE (Ben, 2026-06-01): With a reuseIdentifier the server may route the prompt to an existing
      // session, so events arrive on the resolved id, not ours. Migrate the waiter onto it.
      if (resolvedSessionId !== sessionId) {
        this.sessionStreams.cancelStream(
          sessionId,
          new Error("Session reused; re-registering on the resolved id"),
        );
        resultPromise.catch(() => {
          /* swallow */
        });
        return this.sessionStreams.createStream({
          sessionId: resolvedSessionId,
          onSnapshot: params.onSnapshot,
        });
      }
    } catch (error) {
      this.sessionStreams.cancelStream(
        sessionId,
        error instanceof Error ? error : new Error(String(error)),
      );
      // NOTE (Max, 2026-04-30): resultPromise was rejected by cancelStream above but
      // nobody awaits it since we re-throw. Suppress unhandled rejection.
      resultPromise.catch(() => {
        /* swallow */
      });
      throw error;
    }
    return resultPromise;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private rpc<ResponseType>(
    method: string,
    args: unknown[],
    options?: { timeoutMs?: number },
  ): Promise<ResponseType> {
    if (!this.connection) {
      throw new BedrockApiRequestError({
        code: "REALTIME_NOT_READY",
        status: 0,
        message:
          "Agent connection not ready. Call client.agent.connect() first.",
      });
    }
    return this.connection.callMethod<ResponseType>(method, args, options);
  }
}
